'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { Fanx, Nervinho } from '@/components/CharacterExpressions';

const fanQuotes = [
  { id: 'q-1', quote: '"Maybe this year will be different."', author: 'Fanx, every single season' },
  { id: 'q-2', quote: '"Football is pain. Beautiful, stupid pain."', author: 'Fanx, post-match breakdown' },
  { id: 'q-3', quote: '"I\'m never watching again."', author: 'Fanx (back next match)' },
  { id: 'q-4', quote: '"Referee robbed us. Clearly."', author: 'Fanx, always' },
];

const features = [
  { id: 'f-1', character: 'nervinho' as const, expression: 'overwhelmed' as const, label: 'Emotional Match Tracker' },
  { id: 'f-2', character: 'fanx' as const, expression: 'nervous-smile' as const, label: 'Fan Personality Quizzes' },
  { id: 'f-3', character: 'fanx' as const, expression: 'fake-confidence' as const, label: 'Fanxiety Trivia Challenges' },
  { id: 'f-4', character: 'nervinho' as const, expression: 'trembling' as const, label: 'Nervinho Mood Meter' },
  { id: 'f-5', character: 'nervinho' as const, expression: 'panic-attack' as const, label: 'Match-Day Panic Simulator' },
];

export default function AuthLeftPanel() {
  const [activeQuote, setActiveQuote] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveQuote(prev => (prev + 1) % fanQuotes?.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[520px] shrink-0 p-10 xl:p-12 relative overflow-hidden"
      style={{ backgroundColor: '#0f0f0f' }}
    >
      {/* Background texture dots */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #f0e840 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
      {/* Yellow accent blob */}
      <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #f0e840 0%, transparent 70%)', transform: 'translate(40%, -40%)' }} />
      {/* Logo */}
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden shrink-0" style={{ backgroundColor: '#f0e840' }}>
            <Fanx expression="smug-optimism" size={36} />
          </div>
          <span className="font-black text-xl tracking-tight" style={{ color: '#f5f0e8' }}>
            Fanxiety <span style={{ color: '#f0e840' }}>Club</span>
          </span>
        </div>

        {/* Book cover */}
        <div className="mb-8 relative">
          <div className="rounded-2xl overflow-hidden relative" style={{ border: '2px solid rgba(240,232,64,0.3)', boxShadow: '6px 6px 0px rgba(240,232,64,0.2)' }}>
            <Image
              src="/assets/images/Fanx_and_Nervinho_Book_cover-1778813620901.png"
              alt="Fanxiety Club book cover featuring Fanx the stressed soccer fan and Nervinho the anxious chihuahua"
              width={440}
              height={280}
              className="w-full object-cover"
              style={{ maxHeight: '220px', objectPosition: 'top' }}
            />
            <div className="absolute bottom-0 left-0 right-0 px-4 py-3" style={{ background: 'linear-gradient(to top, rgba(15,15,15,0.95) 0%, transparent 100%)' }}>
              <p className="text-xs font-bold uppercase tracking-widest mb-0.5" style={{ color: '#f0e840' }}>Official Companion Book</p>
              <p className="font-black text-base" style={{ color: '#f5f0e8' }}>Fanxiety Club</p>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-2.5 mb-6">
          {features?.map(f => (
            <div key={f?.id} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(240,232,64,0.12)', border: '1px solid rgba(240,232,64,0.2)' }}>
                {f.character === 'fanx' ? (
                  <Fanx expression={f.expression as import('@/components/CharacterExpressions').FanxExpression} size={26} />
                ) : (
                  <Nervinho expression={f.expression as import('@/components/CharacterExpressions').NervinhoExpression} size={26} />
                )}
              </div>
              <span className="text-sm font-semibold" style={{ color: 'rgba(245,240,232,0.85)' }}>{f?.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Quote rotator */}
      <div className="relative z-10">
        <div className="rounded-2xl p-5 transition-all duration-500" style={{ backgroundColor: 'rgba(240,232,64,0.08)', border: '1px solid rgba(240,232,64,0.2)' }}>
          <div className="mb-3">
            <Nervinho expression="crying-dramatically" size={32} animate />
          </div>
          <p className="text-sm font-bold leading-relaxed mb-3 italic" style={{ color: '#f5f0e8' }}>
            {fanQuotes?.[activeQuote]?.quote}
          </p>
          <p className="text-xs font-medium" style={{ color: 'rgba(240,232,64,0.7)' }}>
            — {fanQuotes?.[activeQuote]?.author}
          </p>
          <div className="flex gap-1.5 mt-4">
            {fanQuotes?.map((_, idx) => (
              <button
                key={`qdot-${idx}`}
                onClick={() => setActiveQuote(idx)}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: idx === activeQuote ? '20px' : '6px',
                  backgroundColor: idx === activeQuote ? '#f0e840' : 'rgba(255,255,255,0.2)',
                }}
                aria-label={`Quote ${idx + 1}`}
                suppressHydrationWarning
              />
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-4">
          <Fanx expression="exhausted" size={20} />
          <p className="text-xs text-center font-medium" style={{ color: 'rgba(245,240,232,0.35)' }}>
            Being emotionally unstable together is more fun.
          </p>
          <Nervinho expression="trembling" size={20} />
        </div>
      </div>
    </div>
  );
}